import Layout from "../../utils/Layout";
import { IObjectConfig } from "../../baseConfigs/layoutConfigShema";
import BaseView from "../BaseView";
import App from "../../App";
import GameEvents from "../../const/GameEvents";
import { Graphics, BitmapText, ObservablePoint } from "pixi.js";
import { TextStyleAlign } from "@pixi/text";
import Utils from "../../utils/Utils";
import { Ease } from '../../Ease';
import { Tween, TweenVars } from '../../Tween';

class BitmapTextField extends BaseView {
    static fontMeasure: any = {};
    protected _fieldWidth: number;
    protected _fieldHeight: number;
    public bound: IObjectConfig;
    public boundShape: Graphics;
    public boundAnchor: {x: number, y: number} = {x: 0, y: 0};
    public initStyle: IBitmapTextFieldStyle;
    public scrollable: boolean = false;
    protected newStyle : IBitmapTextFieldStyle;
    protected _text: BitmapText;
    protected boundsRect: Graphics;
    private definition: { bound?: IObjectConfig , anchor?: {x: number, y: number} };
    public autoApplyBound: boolean;

    constructor(text: string, style: IBitmapTextFieldStyle , definition: { bound?: IObjectConfig , anchor?: {x: number, y: number}}, autoapplyBound: boolean = true)
    {
        super(true);
        this.initStyle = {...style};
        this._text = new BitmapText(text, this.initStyle);
        this.definition = Utils.deepClone(definition);
        this.addChild(this._text);
        this.boundAnchor = definition.anchor;
        if (definition.bound) {
            this.bound = definition.bound;
            this.applyBound();
        }

        this.autoApplyBound = autoapplyBound;
        this.createBoundsRect();
        App.dispatcher.on(GameEvents.UI_UPDATE, () => { this.createBoundsRect(); }, this);
    }

    get anchor(): ObservablePoint {
        // Migration to PIXI v6 uses Observable point, instead of Point
        return this._text.anchor as ObservablePoint;
    }

    set anchor(anchor: ObservablePoint) {
        // Migration to PIXI v6 uses Observable point, instead of Point
        this._text.anchor = anchor;
        this.boundAnchor = anchor;
    }


    get element() {
        return this._text;
    }

    get characters() {
        return this._text.children;
    }

    get text(): string
    {
        return this._text.text;
    }

    set text(text: string)
    {
        text = String(text === '' || text === null || text === undefined ? ' ' : text);
        if (this._text.text === text)
        {
            return;
        }
        // this.dirty = true;
        this._text.text = text;
        if (this.autoApplyBound) {
            this.applyBound();
        }
    }

    set font(font: {fontName:string,fontSize:number} ) {

        // this._text.font = font.font ;
        this._text.fontName = font.fontName ;
        this._text.fontSize = font.fontSize ;
    }

    get font():{fontName:string,fontSize:number}  {
        return {
            fontName:this._text.fontName,
            fontSize:this._text.fontSize
        };
    }



    public applyBound() {
        if (!this.bound ) {
            return;
        }

        if (!this.boundShape) {
            this.boundShape = new Graphics();
            this.boundShape.beginFill(0xFFFB1B, 0);
            this.boundShape.drawRect(0, 0, Layout.scalePixels(this.bound.width), Layout.scalePixels(this.bound.height));
            this.boundShape.endFill();
            this.addChild(this.boundShape);
        } else {
            Layout.testSetWidth(this.boundShape, this.bound.width, false);
            Layout.testSetHeight(this.boundShape, this.bound.height, false);
        }

        if (this.scrollable && !this.mask) {
            this.mask = this.boundShape;
        }


        const scaledWidth = Layout.scalePixels(this.bound.width);
        const scaledHeight = Layout.scalePixels(this.bound.height);
        this.scale.x = 1;
        this.scale.y = 1;
        this._text.scale.x = 1;
        this._text.scale.y = 1;


        if (this._text.width > scaledWidth) {
            Layout.testSetWidth(this._text, this.bound.width);
        }

        if (!this.scrollable && this._text.height > scaledHeight) {
            Layout.testSetHeight(this._text, this.bound.height);
        }

        // if (!this.scrollable && metrix.height > scaledHeight) {
        //     Layout.testSetHeight(this, this.bound.height);
        // }

        Layout.setY(this, this.bound.y);
        Layout.setX(this, this.bound.x );

        Layout.setY(this._text, this.boundAnchor.y * (this.bound.height ));
        Layout.setX(this._text, this.boundAnchor.x * (this.bound.width ));
        this.createBoundsRect();
    }

    public createBoundsRect() {
        if (this.boundsRect && this.boundsRect.parent) {
            this.boundsRect.parent.removeChild(this.boundsRect);
            this.boundsRect.destroy();
        }

        this.boundsRect = new Graphics();
        this.boundsRect.beginFill(0xFF00FF, 0.5);
        this.boundsRect.drawRect(0, 0, Layout.scalePixels(this.bound.width), Layout.scalePixels(this.bound.height));
        this.boundsRect.endFill();
        if (!App.instance.debugBounds) {
            this.boundsRect.alpha = 0;
        }
        this.addChild(this.boundsRect);
    }

    clone(): BitmapTextField {
        const cloning = new BitmapTextField(this.text, this.initStyle, this.definition);
        cloning.pivot.set(0, 0);
        cloning.anchor.set(this.boundAnchor.x, this.boundAnchor.y);
        return cloning;
    }

    public playBounceEffect(duration: number, toScale: number, fromScale?: number, ease: Ease = Ease.BackOut2, customProps?: TweenVars): void {
        if (this.anchor.x !== 0.5) {
            this._text.position.x += this._text.width / 2;
        }
        this.anchor.set(0.5, 0.5);

        if (!fromScale) {
            fromScale = this._text.scale.x;
        }

        Tween.fromTo(this._text.scale, duration, {
            x: fromScale,
            y: fromScale,
        }, {
            x: toScale,
            y: toScale,
            ease: ease,
            ...customProps
        })
    }
}

export interface IBitmapTextFieldStyle {
    fontName: string;
    fontSize?: number;
    align?: TextStyleAlign;
    tint?: number;
    letterSpacing?: number;
    maxWidth?: number;
}

export default BitmapTextField
